import { UserDto } from './sign-on';
import { get, post, put } from './http';

// Types sourced from https://github.com/ev-proddev/user
// Paths below are relative to that repo root (user-dto/src/main/java/com/experticity/user/dto/).

// ── Types ─────────────────────────────────────────────────────────────────────

/** @source UserLookupDto.java */
export type UserLookupDto = {
  identifier: string;
  exists: boolean;
  requiresUpdate: boolean;
};

/** @source EmailDto.java */
export type EmailDto = {
  emailId: string;
  userId: number;
  active: boolean;
  primaryEmail: boolean;
  activationCode?: string;
  activatedTime?: number;
};

/** @source AddressDto.java */
export type AddressDto = {
  userId: number;
  addressId: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  name: string;
  attention: string;
  label: string;
  poBox: boolean;
  military: boolean;
  primary: boolean;
  validated: boolean;
};

/** @source SimpleUserMediaFileDto.java */
export type SimpleUserMediaFile = {
  url: string;
  isDefaultUrl: boolean;
  variations: Record<string, string>;
};

/** @source UserDemographicDto.java */
export type UserDemographicDto = {
  userId: number;
  gender?: string;
  birthDate?: string;
  birthYear?: number;
};

/**
 * @source UserAggregateDto.java
 * @sync-ignore phones — not consumed by onboarding (no SMS verification flows)
 * @sync-ignore localePreference — not consumed by onboarding
 * @sync-ignore profileAttributes — not consumed by onboarding
 * @sync-ignore emailPreferences — not consumed by onboarding
 * @sync-ignore mediaFiles key type — narrowed to known keys (profileAvatar, profileHero); Java uses Map<UserMediaFileType, ...>
 */
export type UserAggregateDto = {
  userDto: UserDto;
  userDemographicDto: UserDemographicDto;
  addressDto: AddressDto;
  emailDtos: EmailDto[];
  mediaFiles: {
    profileAvatar?: SimpleUserMediaFile;
    profileHero?: SimpleUserMediaFile;
  };
};

// ── API ───────────────────────────────────────────────────────────────────────

export function getAuthenticatedUser(): Promise<UserAggregateDto> {
  return get<UserAggregateDto>('/xapi/user/ext/1.0/users/me/complete');
}

export function getPublicUserLookup(searchTerm: string): Promise<UserLookupDto> {
  return get<UserLookupDto>('/xapi/user/pub/2.0/users/publicLookup', { params: { searchTerm } });
}

export function putUserDemographic(data: { userId: number; birthDate?: string }): Promise<void> {
  return put('/xapi/user/ext/1.0/userDemographic', data);
}

export function postRequestVerification(email: string): Promise<void> {
  return post<void>(`/xapi/user/ext/1.0/email/${email}/requestVerification`);
}

export function postVerifyWithCode(email: string, code: string): Promise<void> {
  return post<void>(`/xapi/user/ext/1.0/email/${email}/verifyWithCode`, undefined, { params: { code } });
}
